import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  ExitToApp,
  LocalShipping,
  BarChart,
  Groups,
  ShoppingCart,
  MergeType,
  MergeTwoTone,
  Analytics,
  TrendingUp,
  SmartToy,
  Print,
  History,
  AccountBalanceWallet,
  Devices as DevicesIcon,
  Sync as SyncIcon,
  BarChart as AnalyticsIcon,
  Notifications as NotificationsIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH = 240;

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const userRoles: string[] = user?.roles ?? [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/bi-dashboard', label: 'BI Dashboard', icon: <Dashboard /> },
    { path: '/cash-flow', label: 'Flujo de Caja', icon: <AccountBalanceWallet /> },
    { path: '/financial-analytics', label: 'Analisis Financiero', icon: <BarChart /> },
    { path: '/client-profitability', label: 'Rentabilidad Clientes', icon: <TrendingUp /> },
    { path: '/profitability-chat', label: 'Asistente Rentabilidad', icon: <SmartToy /> },
    { path: '/supply-kpis', label: 'Supply KPIs', icon: <Analytics /> },
    { path: '/effective-coverage', label: 'Effective Coverage', icon: <Analytics /> },
    { path: '/sales-catalog', label: 'Catalogo Ventas', icon: <ShoppingCart /> },
    { path: '/sales-pipeline', label: 'Sales Pipeline', icon: <TrendingUp /> },
    { path: '/toner-dispatch-report', label: 'Despacho Toner', icon: <LocalShipping /> },
    { path: '/model-merge', label: 'Merge MFP Models', icon: <MergeType /> },
    { path: '/customers', label: 'Clientes', icon: <Groups /> },
    { path: '/client-merge', label: 'Merge Clients', icon: <MergeTwoTone /> },
    { path: '/equipment', label: 'Equipos MFP', icon: <Print /> },
    { path: '/inventory-adjustments', label: 'Ajustes Inventario', icon: <History /> },
    { path: '/fleet-clients', label: 'Fleet - Clientes', icon: <Print /> },
    { path: '/fleet-agents', label: 'Fleet - Agentes', icon: <DevicesIcon /> },
    { path: '/fleet-ad-sync', label: 'Fleet - Sync AD', icon: <SyncIcon /> },
    { path: '/fleet-analytics', label: 'Fleet - Analytics', icon: <AnalyticsIcon /> },
    { path: '/fleet-alerts', label: 'Fleet - Alertas', icon: <NotificationsIcon /> },
    // The on-prem product is operated by Servicio Técnico as well as ADMIN.
    // Items without `roles` are ADMIN-only (the default for this app).
    {
      path: '/onprem-clients',
      label: 'Clientes On-Prem',
      icon: <CloudOffIcon />,
      roles: ['ADMIN', 'SERVICIO_TECNICO_ADMIN'],
    },
    { path: '/users', label: 'Users', icon: <People /> },
    { path: '/companies', label: 'Companies', icon: <Business /> }
  ];
  const visibleItems = navItems.filter((item) =>
    ((item as { roles?: string[] }).roles ?? ['ADMIN']).some((r) => userRoles.includes(r)),
  );

  return (
    <>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          ml: `${DRAWER_WIDTH}px`,
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            CDP Admin
          </Typography>
          <Button
            color="inherit"
            startIcon={<ExitToApp />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: '#1976d2',
            color: 'white'
          },
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 'bold' }}>
            CDP
          </Typography>
        </Toolbar>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
        <List>
          {visibleItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.16)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.24)',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: location.pathname === item.path ? 600 : 400
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
}

export { DRAWER_WIDTH };
